import { ROUTE_NAME, STORE } from '~/constants/strings'
import type { TBackground } from '~/constants/types/charts'
import { api } from '~/services'
import { useProjectDataVisualizationsStore } from './projectDataVisualizations'
import { HNRV2 } from '~/helpers'
import { AxiosError } from 'axios'
import { notifyFailure } from '~/utils/notifications'
import {
  EnumerationsDataVizSortBy,
  EnumerationsSortDirection,
  EnumerationsSignificanceFilter,
  type DataVizTileStrataTileDataDTO,
  type TransferObjectsSignificantResultsSummaryDTO,
  type TransferObjectsViewByOptions,
  type QueriesGroups,
  type DataVizMeritVariableDTO,
  type QueriesGetProportionalDataVisualizationResponse,
  type QueriesProportionalTopLevelTileData,
  type QueriesGetProportionalStatisticalValueSummaryRequest,
  EnumerationsProportionalAnalyses,
} from 'client/sratool'
import type {
  SignificantResultsSummary,
  SummaryHashTable,
  SignificantResultsSummaryTable,
  StatisticalValuesSummaryTable,
  PickByFullKeyPattern,
  StatisticalValuesSummaryTableSortColumn,
  NoncompetitivePromotionsTiles,
  NoncompetitivePersonnelActivityType,
} from '~/constants/types/projectDataVisualizations'
import { useChartRefresh } from '~/composables/chart/useChartRefresh'
import type { RouteNamedMap } from 'vue-router/auto-routes'
import {
  GET_DATA_FAILED,
  GET_SIGNIFICANT_RESULTS_SUMMARY_TABLE_FAILED,
  GET_STATISTICAL_VALUE_SUMMARY_TABLE_FAILED,
  REFRESH_SIGNIFICANT_RESULTS_SUMMARY_TABLE_FAILED,
  REFRESH_STATISTICAL_VALUE_SUMMARY_TABLE_FAILED,
} from '~/constants/projectDataVisualizations'
import { useFrequencyAndSeverityWithOverUnderDataBarCharts } from '~/composables/chart/useFrequencyAndSeverityWithOverUnderDataBarCharts'
import {
  useProjectDataVisualizationSelectionStore,
  type SelectionSnapshot,
} from './projectDataVisualizationsSelection'

export const useNoncompetitiveMovementsDataVisualizationsStore = defineStore(
  STORE.PROPORTIONAL_ANALYSES_DATA_VISUALIZATIONS,
  () => {
    const paType = ref<NoncompetitivePersonnelActivityType>()
    const data = ref<QueriesGetProportionalDataVisualizationResponse>()

    const wfaAPI = api.workforceAnalyticsDataVisualizations

    const ProjectDataVisualizationsStore = useProjectDataVisualizationsStore()

    const { getAnalysisData, isCurrent } =
      useProjectDataVisualizationSelectionStore()
    const {
      frequencyOfStatisticalImpactCharts,
      standardDeviationSeverityCharts,
    } = useFrequencyAndSeverityWithOverUnderDataBarCharts({
      selectedGroup: ProjectDataVisualizationsStore.selectedGroup,
      chartData: computed(() => data.value?.frequencyAndSeverityCharts),
    })

    const getTilesAndBarCharts = async (
      selection: SelectionSnapshot,
      requestValidation?: typeof isCurrent
    ): Promise<QueriesGetProportionalDataVisualizationResponse | undefined> => {
      const selectedAnalysis = getAnalysisData(
        selection.analysisID,
        selection.proportionalAnalysisTypeID
      )

      if (!selectedAnalysis) {
        return
      }

      const response = await HNRV2({
        fn: () =>
          wfaAPI.apiSraDataVisualizationsProportionalTileDataPost({
            analysisID: selectedAnalysis.analysisID,
            proportionalAnalysisID: selectedAnalysis.proportionalAnalysisID,
            strataFieldGUID: selection?.strataID,
            strataValue: selection?.strataValue,
            groupFieldGUID: selection?.groupFieldID,
          }),
      })

      if (requestValidation && !requestValidation(selection)) {
        return
      }

      if (response instanceof AxiosError) {
        notifyFailure(GET_DATA_FAILED)
        data.value = undefined
        return
      }

      switch (selectedAnalysis.proportionalAnalysisID) {
        case EnumerationsProportionalAnalyses.NonCompetitivePromotionsAnalysis:
          paType.value = {
            ShortName: 'Promo',
            LongName: 'Promotions',
          }
          break
        case EnumerationsProportionalAnalyses.AllTerminationsAnalysis:
        case EnumerationsProportionalAnalyses.VoluntaryTerminationsAnalysis:
        case EnumerationsProportionalAnalyses.InvoluntaryTerminationsAnalysis:
          paType.value = {
            ShortName: 'Term',
            LongName: 'Terminations',
          }
          break
        default:
          paType.value = undefined
      }

      data.value = response
      return response
    }

    /**
     * Transforms QueriesTopLevelTileData into an array of ITilesData for DataVizTiles.vue
     * @param tileData QueriesTopLevelTileData
     * @returns ITilesData[]
     */
    const strataBackgroundColors: TBackground[] = [
      'dark-gray',
      'orange',
      'purple',
      'blue-green',
      'red',
    ]
    function transformTopLevelTileDataToTiles(
      tileData: QueriesProportionalTopLevelTileData | undefined
    ): NoncompetitivePromotionsTiles[] {
      if (!tileData || !paType.value) return []
      // Map each top-level property to a tile
      return [
        {
          title: `Total ${paType.value.LongName}`,
          numberDisplay: tileData.totalPersonnelActions,
          icon: 'fork_right',
          background: 'blue',
          subText: `Eligible ${tileData.percentagePersonnelActionRate}% ${paType.value?.ShortName} rate`,
        },

        // Strata tiles (example for up to 5)
        ...[1, 2, 3, 4, 5].reduce<NoncompetitivePromotionsTiles[]>(
          (acc, index) => {
            const strata: DataVizTileStrataTileDataDTO =
              tileData[
                `strata${index}Tile` as keyof PickByFullKeyPattern<
                  QueriesProportionalTopLevelTileData,
                  `strata${string}Tile`
                >
              ]
            if (!strata) return acc

            acc.push({
              title: `Significant Results: ${strata.strataDisplayName}`,
              numberDisplay: `${strata.significantStrataPercentage}%`,
              icon: 'layers',
              background: strataBackgroundColors[index],
              subText: `Total Analyses: ${strata.totalStrataCount}`,
              strataNumber: index,
            })

            return acc
          },
          []
        ),
      ]
    }

    const tiles = computed(() => {
      return transformTopLevelTileDataToTiles(data.value?.topLevelTileData)
    })

    const meritVariables = ref<DataVizMeritVariableDTO[]>([])

    const significantResultsSummaryTables = ref<
      SummaryHashTable<SignificantResultsSummaryTable>
    >({})

    const buildSignificantResultsHierarchy = (
      rows: TransferObjectsSignificantResultsSummaryDTO[]
    ) => {
      const hashTable = Object.create(null)
      rows.forEach(
        (row) =>
          (hashTable[row.hierarchy] = {
            hierarchyID: row.hierarchy,
            data: row,
            children: [],
            expanded: false,
          })
      )
      const dataTree: Array<SignificantResultsSummary> = []
      rows.forEach((row) => {
        if (row.parentNode) {
          hashTable[row.parentNode].children.push(hashTable[row.hierarchy])
        } else {
          dataTree.push(hashTable[row.hierarchy])
        }
      })
      return dataTree
    }

    const loadSignificantResultsSummaryTables = async (
      selection: SelectionSnapshot,
      requestValidation?: typeof isCurrent
    ) => {
      const numberOfResults = 10
      const sortByID: EnumerationsDataVizSortBy =
        EnumerationsDataVizSortBy.HighestStatValue

      const selectedAnalysis = getAnalysisData(
        selection.analysisID,
        selection.proportionalAnalysisTypeID
      )

      if (!selectedAnalysis) {
        return
      }

      const response = await HNRV2({
        fn: () =>
          wfaAPI.apiSraDataVisualizationsProportionalSignificantResultsSummaryPost(
            {
              analysisID: selectedAnalysis.analysisID,
              proportionalAnalysisID: selectedAnalysis?.proportionalAnalysisID,
              numberOfResults,
              sortByID,
              strataFieldGUID: selection?.strataID,
              strataValuesFilter: selection.strataValue
                ? [selection.strataValue]
                : null,
              groupFieldGUID: selection?.groupFieldID,
            }
          ),
      })

      if (requestValidation && !requestValidation(selection)) {
        return
      }

      if (response instanceof AxiosError) {
        meritVariables.value = []
        significantResultsSummaryTables.value = {}

        notifyFailure(GET_SIGNIFICANT_RESULTS_SUMMARY_TABLE_FAILED)
        return
      }

      meritVariables.value = response.meritVariables

      significantResultsSummaryTables.value =
        ProjectDataVisualizationsStore.groupOptions.reduce<
          SummaryHashTable<SignificantResultsSummaryTable>
        >((acc, currentGroup) => {
          const isNotSelectedGroupFilter =
            ProjectDataVisualizationsStore.selectedGroup &&
            currentGroup.groupFieldGUID !==
              ProjectDataVisualizationsStore.selectedGroup.groupFieldGUID

          const responseDataForGroup =
            response.significantResultsSummaryDataResponse.filter(
              (row) => row.groupFieldGUID === currentGroup.groupFieldGUID
            )

          if (isNotSelectedGroupFilter || responseDataForGroup.length === 0) {
            return acc
          }

          acc[currentGroup.groupDisplayName] = {
            groupField: currentGroup,
            data: buildSignificantResultsHierarchy(
              responseDataForGroup[0].significantResultsSummaryData
            ),
          }

          return acc
        }, {})
    }

    const refreshSignificantResultsSummaryTable = async (
      selection: SelectionSnapshot,
      selectedSortByOption: EnumerationsDataVizSortBy,
      groupField: QueriesGroups,
      selectedViewByOption?: TransferObjectsViewByOptions,
      requestValidation?: typeof isCurrent
    ) => {
      const numberOfResults = 10
      const selectedAnalysis = getAnalysisData(
        selection.analysisID,
        selection.proportionalAnalysisTypeID
      )

      if (!selectedAnalysis) {
        return
      }

      const response = await HNRV2({
        fn: () =>
          wfaAPI.apiSraDataVisualizationsRefreshProportionalSignificantResultsSummaryPost(
            {
              analysisID: selectedAnalysis.analysisID,
              proportionalAnalysisID: selectedAnalysis?.proportionalAnalysisID,
              numberOfResults,
              sortByID: selectedSortByOption,
              strataValuesFilter: selection.strataValue
                ? [selection.strataValue]
                : null,
              groupFieldGUID: groupField.groupFieldGUID,
              viewByStrataFieldGUID: selectedViewByOption?.strataGUID,
            }
          ),
      })

      if (requestValidation && !requestValidation(selection)) {
        return
      }

      if (response instanceof AxiosError) {
        notifyFailure(REFRESH_SIGNIFICANT_RESULTS_SUMMARY_TABLE_FAILED)
        return
      }

      significantResultsSummaryTables.value[groupField.groupDisplayName] = {
        groupField: groupField,
        data: buildSignificantResultsHierarchy(
          response.significantResultsSummaryData
        ),
      }
    }

    const viewByOptions = ref<TransferObjectsViewByOptions[]>([])

    const statisticalValuesSummaryTables = ref<
      SummaryHashTable<StatisticalValuesSummaryTable>
    >({})

    const loadStatisticalValuesSummaryTables = async (
      selection: SelectionSnapshot,
      requestValidation?: typeof isCurrent
    ) => {
      const selectedAnalysis = getAnalysisData(
        selection.analysisID,
        selection.proportionalAnalysisTypeID
      )

      if (!selectedAnalysis) {
        return
      }

      const response = await HNRV2({
        fn: () =>
          wfaAPI.apiSraDataVisualizationsProportionalStatisticalValueSummaryPost(
            {
              analysisID: selectedAnalysis?.analysisID,
              proportionalAnalysisID: selectedAnalysis?.proportionalAnalysisID,
              viewByStrataFieldGUID: selection?.strataID,
              strataValuesFilter: selection.strataValue
                ? [selection.strataValue]
                : null,
              groupFieldGUID: selection?.groupFieldID,
              getAllRecords: false,
              significanceFilter: EnumerationsSignificanceFilter.Significant,
            }
          ),
      })

      if (requestValidation && !requestValidation(selection)) {
        return
      }

      if (response instanceof AxiosError) {
        viewByOptions.value = []
        statisticalValuesSummaryTables.value = {}

        notifyFailure(GET_STATISTICAL_VALUE_SUMMARY_TABLE_FAILED)
        return
      }

      viewByOptions.value = response.viewByOptions

      statisticalValuesSummaryTables.value =
        ProjectDataVisualizationsStore.groupOptions.reduce<
          SummaryHashTable<StatisticalValuesSummaryTable>
        >((acc, currentGroup) => {
          const isNotSelectedGroupFilter =
            ProjectDataVisualizationsStore.selectedGroup &&
            currentGroup.groupFieldGUID !==
              ProjectDataVisualizationsStore.selectedGroup.groupFieldGUID
          const responseDataForGroup =
            response.statisticalValuesSummaryDataResponse.filter(
              (table) => table.groupFieldGUID === currentGroup.groupFieldGUID
            )
          if (isNotSelectedGroupFilter || responseDataForGroup.length === 0) {
            return acc
          }

          acc[currentGroup.groupDisplayName] = {
            groupField: currentGroup,
            data: responseDataForGroup[0],
          }

          return acc
        }, {})
    }

    const refreshStatisticalValuesSummaryTable = async (
      selection: SelectionSnapshot,
      selectedViewByOption: TransferObjectsViewByOptions,
      selectedMinimumSignificanceOption: EnumerationsSignificanceFilter,
      groupField: QueriesGroups,
      currentPage: number,
      sortColumn: StatisticalValuesSummaryTableSortColumn = null,
      sortDirection: EnumerationsSortDirection = EnumerationsSortDirection.Ascending,
      requestValidation?: typeof isCurrent
    ) => {
      const selectedAnalysis = getAnalysisData(
        selection.analysisID,
        selection.proportionalAnalysisTypeID
      )

      if (!selectedAnalysis) {
        return
      }

      const payload: QueriesGetProportionalStatisticalValueSummaryRequest = {
        analysisID: selectedAnalysis?.analysisID,
        proportionalAnalysisID: selectedAnalysis?.proportionalAnalysisID,
        viewByStrataFieldGUID: selectedViewByOption.strataGUID,
        strataValuesFilter: selection.strataValue
          ? [selection.strataValue]
          : null,
        groupFieldGUID: groupField.groupFieldGUID,
        significanceFilter: selectedMinimumSignificanceOption,
        page: currentPage,
        pageSize: 10,
        sortColumn: sortColumn,
        sortDirection: sortDirection,
      }

      const response = await HNRV2({
        fn: () =>
          wfaAPI.apiSraDataVisualizationsRefreshProportionalStatisticalValueSummaryAnalysisIDPost(
            selectedAnalysis?.analysisID as string,
            payload
          ),
      })

      if (requestValidation && !requestValidation(selection)) {
        return
      }

      if (response instanceof AxiosError) {
        notifyFailure(REFRESH_STATISTICAL_VALUE_SUMMARY_TABLE_FAILED)
        return
      }

      statisticalValuesSummaryTables.value[groupField.groupDisplayName] = {
        groupField: groupField,
        data: response,
      }
    }

    const getProportionalAnalysesCharts = async (
      selection: SelectionSnapshot
    ) => {
      ProjectDataVisualizationsStore.loading.chart = true

      resetData()

      await Promise.all([
        // tiles
        // bar charts
        getTilesAndBarCharts(selection, isCurrent),

        // sign results summary table
        loadSignificantResultsSummaryTables(selection, isCurrent),

        // stat values summary table
        loadStatisticalValuesSummaryTables(selection, isCurrent),
      ])

      ProjectDataVisualizationsStore.loading.chart = false
    }

    useChartRefresh({
      fetchChartFunction: getProportionalAnalysesCharts,
      routeName:
        ROUTE_NAME.NON_COMPETITIVE_MOVEMENTS_DATA_VISUALIZATIONS as keyof RouteNamedMap,
    })

    const resetData = () => {
      data.value = undefined
      statisticalValuesSummaryTables.value = {}
      significantResultsSummaryTables.value = {}
    }

    return {
      resetData,
      // ready for display
      tiles,
      transformTopLevelTileDataToTiles,

      frequencyOfStatisticalImpactCharts,

      standardDeviationSeverityCharts,

      viewByOptions,
      statisticalValuesSummaryTables,

      meritVariables,
      significantResultsSummaryTables,

      getTilesAndBarCharts,
      getProportionalAnalysesCharts,
      refreshSignificantResultsSummaryTable,
      refreshStatisticalValuesSummaryTable,

      // raw
      data,
    }
  }
)
